import { motion } from "framer-motion";
import { useRegisterForm } from "../hooks/use-register-form";
import { AuthInput } from "../components/AuthInput";
import { Checkbox } from "../components/Checkbox";
import { AuthButton } from "../components/AuthButton";
import { AuthFieldSection } from "../components/AuthFieldSection";
import { MdEmail, MdLock } from "react-icons/md";
import type { RegisterFormValues } from "../auth.types";

export interface RegisterFormProps {
  emailFieldTitle?: string;
  emailFieldDescription?: string;
  passwordFieldTitle?: string;
  passwordFieldDescription?: string;
  repeatPasswordFieldTitle?: string;
  repeatPasswordFieldDescription?: string;
  acceptTermsLabel?: string;
  submitLabel?: string;
  onSubmit?: (values: RegisterFormValues) => Promise<void>;
}

export const RegisterForm = ({
  emailFieldTitle = "Adresă de email",
  emailFieldDescription = "Adresa ta de email va fi folosită pentru autentificare și notificări importante.",
  passwordFieldTitle = "Parolă",
  passwordFieldDescription = "Alege o parolă sigură cu cel puțin 8 caractere, incluzând litere și cifre.",
  repeatPasswordFieldTitle = "Confirmă parola",
  repeatPasswordFieldDescription = "Introdu din nou parola pentru a confirma că este corectă.",
  acceptTermsLabel = "Accept Termenii și Condițiile",
  submitLabel = "Creează cont",
  onSubmit: onSubmitFromProps,
}: RegisterFormProps) => {
  const {
    values,
    errors,
    isSubmitting,
    handleChange,
    handleCheckboxChange,
    handleSubmit,
  } = useRegisterForm({ onSubmit });

  async function onSubmit(values: RegisterFormValues) {
    if (onSubmitFromProps) {
      await onSubmitFromProps(values);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="flex flex-col gap-5"
      aria-label="Register form"
    >
      <AuthFieldSection
        icon={<MdEmail className="text-[25px]" />}
        title={emailFieldTitle}
        description={emailFieldDescription}
        animationIndex={0}
      >
        <AuthInput
          id="email"
          name="email"
          type="email"
          value={values.email}
          placeholder="ernestomop@gmail.com"
          onChange={handleChange}
          error={errors.email}
          autoComplete="off"
          aria-label="Email address"
        />
      </AuthFieldSection>

      <AuthFieldSection
        icon={<MdLock className="text-[25px]" />}
        title={passwordFieldTitle}
        description={passwordFieldDescription}
        animationIndex={1}
      >
        <AuthInput
          id="password"
          name="password"
          type="password"
          value={values.password}
          placeholder="XXXXXXXX"
          onChange={handleChange}
          error={errors.password}
          autoComplete="off"
          aria-label="Password"
        />
        <AuthInput
          id="repeatPassword"
          name="repeatPassword"
          type="password"
          value={values.repeatPassword}
          placeholder={repeatPasswordFieldTitle}
          onChange={handleChange}
          error={errors.repeatPassword}
          autoComplete="off"
          aria-label="Repeat password"
        />
      </AuthFieldSection>

      <motion.div
        className="flex flex-col gap-4 my-1"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.5, ease: "easeOut" }}
      >
        <Checkbox
          id="acceptTerms"
          name="acceptTerms"
          checked={values.acceptTerms}
          onChange={handleCheckboxChange}
          label={acceptTermsLabel}
        />
        {errors.acceptTerms && (
          <motion.p
            initial={{ opacity: 0, y: -5, x: -3 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.22, ease: "backOut" }}
            className="text-xs text-red-400 pl-2 flex items-center gap-1.5"
            role="alert"
          >
            <motion.span
              className="inline-block w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0"
              animate={{ opacity: [1, 0.3, 1], scale: [1, 0.7, 1] }}
              transition={{
                duration: 1.0,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            {errors.acceptTerms}
          </motion.p>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.5, ease: "easeOut" }}
      >
        <AuthButton
          label={submitLabel}
          type="submit"
          fullWidth
          isLoading={isSubmitting}
          disabled={isSubmitting}
        />
      </motion.div>
    </form>
  );
};
